import { useState } from 'react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip } from '@/shared/ui/states'
import { EmptyRow, ErpTable, MetricCard, KpiStrip, Panel, SectionHeader, Td, Th }
  from '@/components/erp/primitives'
import { Pill, fmtDate } from './components/primitives'
import { applyImport, uploadImport, type ImportRun } from './api'

interface RowPlan {
  line: number
  displayName: string | null
  disposition: string
  reason: string | null
}

/**
 * Bulk import — <b>two acts, never one</b>.
 *
 * Uploading produces a DRY-RUN REPORT and writes nothing. A separate Apply commits it. A
 * 5,000-row file whose fiftieth row would open a second file on somebody already in the
 * register has to be inspectable first, and "apply with a rollback if it goes wrong" is not
 * the same thing — by then the operator has already been told it worked.
 *
 * The rejected rows carry their LINE NUMBER. "Row rejected: invalid item type" against a
 * five-thousand-row file is not something anybody can act on.
 */
export function ImportPage() {
  const [formatCode, setFormatCode] = useState('CSV')
  const [run, setRun] = useState<ImportRun | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function upload(file: File) {
    setBusy(true)
    setError(null)
    try {
      setRun(await uploadImport(file, formatCode))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  async function commit() {
    if (!run) return
    setBusy(true)
    setError(null)
    try {
      // Deliberately NOT a redirect. The applied report says which rows landed and which
      // failed on the way in; navigating away replaces that with a subject list that looks
      // the same whether every row worked or four of them did not.
      setRun(await applyImport(run.id))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const plans: RowPlan[] = (() => {
    if (!run?.reportJson) return []
    try {
      return (JSON.parse(run.reportJson) as { rows?: RowPlan[] }).rows ?? []
    } catch {
      return []
    }
  })()

  const applied = run?.statusCode === 'APPLIED'

  return (
    <div className="max-w-4xl space-y-3">
      <SectionHeader
        title="Bulk import"
        description="Uploading produces a dry run and writes nothing. Applying it is a second,
                     explicit act."
      />

      {error && <ErrorStrip message={error} />}

      <Panel>
        <div className="flex flex-wrap items-end gap-2 p-3">
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Format
            </span>
            <select value={formatCode} onChange={(e) => setFormatCode(e.target.value)}
                    className="h-7 rounded border border-input bg-background px-2 text-[12px]">
              <option value="CSV">CSV</option>
              <option value="JSON">JSON</option>
            </select>
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              File
            </span>
            <input type="file" accept=".csv,.json,text/csv,application/json" disabled={busy}
                   onChange={(e) => {
                     const file = e.target.files?.[0]
                     if (file) void upload(file)
                   }}
                   className="text-[12px]" />
          </label>
          <p className="text-[11px] text-muted-foreground">
            Columns: subjectTypeCode, subjectRef, slug, displayName, itemTypeCode, title,
            body, url, occurredAt, sourceCode, legalBasisCode, infoCredibility
          </p>
        </div>
      </Panel>

      {run && (
        <>
          <KpiStrip>
            <MetricCard label="Rows parsed" value={run.rowsParsed} />
            <MetricCard label={applied ? 'Files opened' : 'Would create'} value={run.rowsCreate}
                        sub="New files opened" />
            <MetricCard label={applied ? 'Files added to' : 'Would match'} value={run.rowsMatch}
                        sub="Existing files added to" />
            <MetricCard label={applied ? 'Failed' : 'Rejected'} value={run.rowsReject}
                        sub="With the line number and the reason" />
          </KpiStrip>

          <Panel>
            <div className="flex flex-wrap items-center justify-between gap-2 p-2">
              <div className="text-[11px] text-muted-foreground">
                <Pill className="border-border">{run.statusCode}</Pill>
                <span className="ml-2">
                  {run.statusCode === 'APPLIED'
                    ? `Applied ${fmtDate(run.appliedAt)}`
                    // A report describes the database as it was when it was produced.
                    : `This dry run expires ${fmtDate(run.expiresAt)} — after that, upload
                       again for a current report.`}
                </span>
              </div>
              {run.statusCode === 'DRY_RUN' && (
                <button type="button" disabled={busy} onClick={() => void commit()}
                        className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                                   text-primary-foreground disabled:opacity-50">
                  {busy ? 'Applying…' : `Apply — create ${run.rowsCreate}, add to ${run.rowsMatch}`}
                </button>
              )}
            </div>
          </Panel>

          <Panel>
            <ErpTable head={<>
              <Th className="text-right">Line</Th>
              <Th>Subject</Th>
              <Th>{applied ? 'What happened' : 'What would happen'}</Th>
              <Th>Reason</Th>
            </>}>
              {plans.length === 0 && (
                <EmptyRow colSpan={4} message="The report carried no row detail." />
              )}
              {plans.map((plan) => (
                <tr key={plan.line} className="hover:bg-muted/40">
                  <Td className="text-right tabular-nums">{plan.line}</Td>
                  <Td>{plan.displayName ?? <span className="text-muted-foreground">—</span>}</Td>
                  <Td>
                    <Pill className={
                      plan.disposition === 'REJECT' || plan.disposition === 'FAILED'
                      ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
                      : plan.disposition === 'CREATE' || plan.disposition === 'CREATED'
                      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                      : 'border-border'}>
                      {plan.disposition}
                    </Pill>
                  </Td>
                  <Td>
                    <span className="text-[11px] text-muted-foreground">
                      {plan.reason ?? ''}
                    </span>
                  </Td>
                </tr>
              ))}
            </ErpTable>
          </Panel>
        </>
      )}
    </div>
  )
}
